<template>
  <div class="col-12 rounded-button">
    <div class='button-inner' v-if="hasState">
      <div
        ref="roundedButton"
        :style="[
        propStyle,
        isEnabled,
        isEnabledWithOutline,
        isDisabled,
        isDisabledWithOutline,
        isSelected,
        isSelectedWithOutline,
        isLoading,
        isChecked,
        isOutlin,
        hasShadow ? shadowClass : '']"
        :class="['button-wrapper row no-gutters align-items-center justify-content-center btn', state, isLessonFlow ? 'is-lesson-footer px-1' : 'px-3']"
        @click="[state === 'disabled' || state === 'loading' ? '' : buttonClicked()]"
        @mouseenter="hover('in')"
        @mouseleave="hover('out')"
        :tabindex="this.isDisabled || this.isDisabledWithOutline ? '-1' : '0'"
        @keyup.enter="[state === 'disabled' || state === 'loading' ? '' : buttonClicked()]"
        :aria-hidden="isDisabled || isDisabledWithOutline ? true : false"
        :aria-label="iconName ? null : $t(text)"
      >
        <div v-if="state === 'loading' && !loadingRingOrRow" class="col-auto btn-loading btn-loading-ring">
          <loader :size="loadStyle.size" :color="loadStyle.color" :stroke="loadStyle.stroke"></loader>
        </div>
        <div v-if="state === 'loading' && loadingRingOrRow" class="col btn-loading">
          <content-loader
            :width="loadRowStyle.width"
            :height="loadRowStyle.height"
            :speed="1"
            primaryColor="rgba(0, 0, 0, .05)"
            secondaryColor="rgba(0, 0, 0, .15)"
          >
            <rect
              x="0"
              y="2"
              rx="0"
              ry="0"
              :width="loadRowStyle.width"
              :height="loadRowStyle.height-4"
            />
          </content-loader>
        </div>
        <div :class='["button-content", isLocked ? "locked-button" : ""]'>
          <div class="icon-container" v-if="iconType === 'svg' || iconType === 'img'">
            <div v-if="iconType === 'svg'" class="icon-svg" :style="iconSize">
              <component
                :is="iconName"
                :color="getColors.textColor"
                :hoverColor="getColors.hoverTextColor"
                :hovered="iconHovered"
                class="svg-icon"
              ></component>
            </div>
            <div v-if="iconType === 'img'" class="icon-img">
              <img
                v-if="iconName !== '' && iconType === 'img'"
                :src="[iconName ? `/static/img/icons/${iconName}.png` : '']"
              />
            </div>
          </div>
          <div
          v-html="$t(text)"
          :class="[iconName !== '' ? 'button-text' : 'btn-text', isLocked ? '' : 'col', 'btn-html-text']"
          v-if="state !== 'loading' && state !== 'checked' && isTextVhtml"> </div>
          <div
            ref="content-wrapper"
            :class="[iconName !== '' ? 'button-text' : 'btn-text', isLocked ? '' : 'col', isLessonFlow ? 'is-lesson-footer' : '']"
            v-if="state !== 'loading' && state !== 'checked' && !isTextVhtml"
          >
              <template ref="textContent" v-if="!!text" tabindex="-1" :aria-label="$t(text)">
                <span aria-hidden="true">{{$t(text)}}</span>
              </template>
              <template ref="htmlContent" v-else>
                <slot></slot>
              </template>
          </div>
          <img v-if="isLocked" class="locked-icon" src="/static/img/icons/pro-label.svg" :aria-label="$t('app.tabs.store')" :alt="$t('app.tabs.store')"/>
        </div>
        <div v-if="state === 'checked'" class="col icon-checked">
          <icon-checkmark :color="getColors.textColor" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LoaderRing from '@/components/shared/elements/loaders/LoaderRing';
import textFit from 'textfit';
import { ContentLoader } from 'vue-content-loader';

const IconCheckmark = () => import('@/components/shared/elements/icons/IconCheckMarkLogin');
const IconFacebook = () => import('@/components/shared/elements/icons/IconFacebookLogo');
const IconApple = () => import('@/components/shared/elements/icons/IconAppleLogo');
const IconGoogle = () => import('@/components/shared/elements/icons/IconGoogleLogo');
const IconGoogleLogoColorful = () => import('@/components/shared/elements/icons/IconGoogleLogoColorful');
const IconKretaLogo = () => import('@/components/shared/elements/icons/IconKretaLogo');

export default {
  name: 'RoundedButton',
  components: {
    'icon-checkmark': IconCheckmark,
    loader: LoaderRing,
    'content-loader': ContentLoader,
    facebook: IconFacebook,
    apple: IconApple,
    google: IconGoogle,
    googleColorful: IconGoogleLogoColorful,
    IconKretaLogo,
  },
  mounted() {
    if (!this.text && !this.$slots.default) {
      throw Error('There is no text neither slot. One of them is required');
    }
  },
  props: {
    text: {
      type: String,
      required: false,
    },
    customStyle: {
      type: Object,
      required: false,
    },
    iconType: {
      type: String,
      required: false,
    },
    iconWidth: {
      type: Number,
      required: false,
      default: 24,
    },
    iconHeight: {
      type: Number,
      required: false,
      default: 24,
    },
    iconName: {
      type: [String, Object, Function],
      required: false,
      default: '',
    },
    state: {
      type: String,
      required: false,
      default: 'enabled',
    },
    hasShadow: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOutline: {
      type: [String, Boolean],
      required: false,
      default: false,
    },
    borderWidth: {
      type: Number,
      required: false,
      default: 2,
    },
    fontSize: {
      type: String,
      required: false,
      default: '21px',
    },
    fontWeight: {
      type: String,
      required: false,
      default: '600',
    },
    loadingStyle: {
      type: [Object, Array],
      required: false,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    loadingRingOrRow: {
      type: Boolean,
      default: false,
      required: false,
    },
    loadingRowStyle: {
      type: [Object, Array],
      required: false,
    },
    color: {
      type: String,
      required: false,
      default: 'var(--primaryBlue)',
    },
    customColors: {
      type: Object,
      required: false,
      default: undefined,
    },
    isTextVhtml: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLogin: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      iconHovered: false,
    };
  },
  computed: {
    ...mapGetters({
      getWCAGSettings: 'settings/getWCAGSettings',
    }),
    isLessonFlow() {
      return this.$route.name == 'Lesson' || this.$route.name == 'TeachBot';
    },
    getColors() {
      if (this.customColors != undefined) {
        return this.customColors;
      } else if (this.isOutline) {
        switch (this.color) {
          case '#fab62f':
            return {
              textColor: '#fab62f',
              hoverTextColor: '#fab62f',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(236, 159, 5, 0.1)',
              borderColor: '#fab62f',
              hoverBorderColor: '#ec9f05',
              disabledTextColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBorderColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBackgroundColor: 'transparent',
            };
          case 'var(--primaryBlue)':
            return {
              textColor: 'var(--primaryBlue)',
              hoverTextColor: 'var(--primaryBlue)',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(72, 176, 247, 0.1)',
              borderColor: 'var(--primaryBlue)',
              hoverBorderColor: 'var(--primaryBlue)',
              disabledTextColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBorderColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBackgroundColor: 'transparent',
            };
          case '#83b74e':
            return {
              textColor: '#83b74e',
              hoverTextColor: '#83b74e',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(112, 163, 59, 0.1)',
              borderColor: '#83b74e',
              hoverBorderColor: '#83b74e',
              disabledTextColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBorderColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBackgroundColor: 'transparent',
            };
          case '#ffffff':
            return {
              textColor: '#ffffff',
              hoverTextColor: '#ffffff',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderColor: '#ffffff',
              hoverBorderColor: '#ffffff',
              disabledTextColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBorderColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBackgroundColor: 'transparent',
            };
          case '#4092c9':
            return {
              textColor: '#4092c9',
              hoverTextColor: '#4092c9',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(64, 146, 201, 0.1)',
              borderColor: '#4092c9',
              hoverBorderColor: '#4092c9',
              disabledTextColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBorderColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBackgroundColor: 'transparent',
            };
          case '#757575':
            return {
              textColor: '#757575',
              hoverTextColor: '#757575',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(117, 117, 117, 0.1)',
              borderColor: '#757575',
              hoverBorderColor: '#757575',
              disabledTextColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBorderColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBackgroundColor: 'transparent',
            };
          default: {
            return {
              textColor: '#fab62f',
              hoverTextColor: '#fab62f',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(236, 159, 5, 0.1)',
              borderColor: '#fab62f',
              hoverBorderColor: '#ec9f05',
              disabledTextColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBorderColor: this.getWCAGSettings.contrastMode ? 'var(--darkGrey)' : 'rgba(174, 183, 191, 0.5)',
              disabledBackgroundColor: 'transparent',
            };
          }
        }
      } else {
        switch (this.color) {
          case '#fab62f':
            return {
              textColor: '#333333',
              backgroundColor: '#fab62f',
              hoverTextColor: '#333333',
              hoverBackgroundColor: '#ec9f05',
              disabledTextColor: '#aeb7bf',
              disabledBackgroundColor: '#f0f0f0',
              borderColor: undefined,
              hoverBorderColor: undefined,
              disabledBorderColor: undefined,
            };
          case '#5272a4':
            return {
              textColor: '#ffffff',
              backgroundColor: '#5272a4',
              hoverTextColor: '#ffffff',
              hoverBackgroundColor: '#3e5a87',
              disabledTextColor: '#aeb7bf',
              disabledBackgroundColor: '#f0f0f0',
              borderColor: undefined,
              hoverBorderColor: undefined,
              disabledBorderColor: undefined,
            };

          case 'var(--primaryBlue)':
            return {
              textColor: '#ffffff',
              backgroundColor: 'var(--primaryBlue)',
              hoverTextColor: '#ffffff',
              hoverBackgroundColor: '#389de2',
              disabledTextColor: '#aeb7bf',
              disabledBackgroundColor: '#f0f0f0',
              borderColor: undefined,
              hoverBorderColor: undefined,
              disabledBorderColor: undefined,
            };
          case '#83b74e':
            return {
              textColor: '#ffffff',
              backgroundColor: '#83b74e',
              hoverTextColor: '#ffffff',
              hoverBackgroundColor: '#70a33b',
              disabledTextColor: '#aeb7bf',
              disabledBackgroundColor: '#f0f0f0',
              borderColor: undefined,
              hoverBorderColor: undefined,
              disabledBorderColor: undefined,
            };
          default: {
            return {
              textColor: '#333333',
              backgroundColor: '#fab62f',
              hoverTextColor: '#333333',
              hoverBackgroundColor: '#ec9f05',
              disabledTextColor: '#aeb7bf',
              disabledBackgroundColor: '#f0f0f0',
              borderColor: undefined,
              hoverBorderColor: undefined,
              disabledBorderColor: undefined,
            };
          }
        }
      }
    },
    hasState() {
      return (
        this.state === 'enabled' ||
        this.state === 'checked' ||
        this.state === 'disabled' ||
        this.state === 'loading' ||
        this.state === 'selected'
      );
    },
    enabledClass() {
      return {
        color: this.getColors.textColor,
        borderColor: this.getColors.borderColor,
        backgroundColor: this.getColors.backgroundColor,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        border: this.getColors.borderColor
          ? `${this.borderWidth}px solid ${this.getColors.borderColor}`
          : 'none',
      };
    },
    selectedOutlineClass() {
      return {
        color: this.getColors.textColor,
        borderColor: this.getColors.borderColor,
        backgroundColor: this.getColors.backgroundColor,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        border: this.getColors.borderColor
          ? `${this.borderWidth}px solid ${this.getColors.borderColor}`
          : 'none',
      };
    },
    disabledClass() {
      return {
        color: this.getColors.disabledTextColor,
        backgroundColor: this.getColors.disabledBackgroundColor,
        borderColor: this.getColors.disabledBorderColor,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        border: this.getColors.disabledBorderColor
          ? `${this.borderWidth}px solid ${this.getColors.disabledBorderColor}`
          : 'none',
      };
    },
    disabledOutlineClass() {
      return {
        color: this.getColors.disabledTextColor,
        borderColor: this.getColors.disabledBorderColor,
        backgroundColor: this.getColors.disabledBackgroundColor,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        border: this.getColors.disabledBorderColor
          ? `${this.borderWidth}px solid ${this.getColors.disabledBorderColor}`
          : 'none',
      };
    },
    enabledOutlineClass() {
      return {
        color: this.getColors.textColor,
        borderColor: this.getColors.borderColor,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        border: this.getColors.borderColor
          ? `${this.borderWidth}px solid ${this.getColors.borderColor}`
          : 'none',
      };
    },
    outlineClass() {
      return {
        color: this.getColors.textColor,
        borderColor: this.getColors.borderColor,
        backgroundColor: this.getColors.backgroundColor,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        border: this.getColors.borderColor
          ? `${this.borderWidth}px solid ${this.getColors.borderColor}`
          : 'none',
      };
    },
    shadowClass() {
      return {
        boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
      };
    },
    isChecked() {
      return this.state === 'checked' && this.isOutline === false
        ? this.enabledClass
        : '';
    },
    isSelected() {
      return this.state === 'selected' && this.isOutline === false
        ? this.enabledClass
        : '';
    },
    isEnabled() {
      return this.state === 'enabled' && this.isOutline === false
        ? this.enabledClass
        : '';
    },
    isDisabled() {
      return this.state === 'disabled' && this.isOutline === false
        ? this.disabledClass
        : '';
    },
    isSelectedWithOutline() {
      return this.state === 'selected' && this.isOutline
        ? this.selectedOutlineClass
        : '';
    },
    isDisabledWithOutline() {
      return this.state === 'disabled' && this.isOutline
        ? this.disabledOutlineClass
        : '';
    },
    isEnabledWithOutline() {
      return this.state === 'enabled' && this.isOutline
        ? this.enabledOutlineClass
        : '';
    },
    isOutlin() {
      return this.isOutline && this.isDisabled === false
        ? this.outlineClass
        : '';
    },
    isLoading() {
      return this.state === 'loading' && this.isOutline === false
        ? this.enabledClass
        : '';
    },
    iconSize() {
      return {
        width: `${this.iconWidth}px`,
        height: `${this.iconHeight}px`,
      };
    },
    propStyle() {
      return {
        ...this.customStyle,
      };
    },
    loadStyle() {
      if (this.loadingStyle) {
        return {
          ...this.loadingStyle,
        };
      }
      return {
        color: this.color == '#fab62f' ? '#333333' : '#fff',
        size: 30,
        stroke: 4,
      };
    },
    loadRowStyle() {
      if (this.loadingRowStyle) {
        return {
          ...this.loadingRowStyle,
        };
      }
      return {
        width: 24,
        height: 8.25,
        x: 0,
        y: 0,
      };
    },
  },
  methods: {
    focus() {
      if (this.$refs.roundedButton) this.$refs.roundedButton.focus();
    },
    buttonClicked() {
      if (this.isLogin) {
        this.iconHovered = false;
        this.$refs.roundedButton.style.backgroundColor = this.getColors.backgroundColor;
        this.$refs.roundedButton.style.color = this.getColors.textColor;
        this.$refs.roundedButton.style.borderColor = this.getColors.borderColor;
      }
      if (this.isChecked || this.state === 'checked') {
        return;
      }
      this.$emit('roundedButtonClicked');
    },
    hover(mode) {
      if (!this.$refs.roundedButton) {
        return;
      }
      if (
        this.state === 'loading' ||
        this.state === 'disabled' ||
        this.state === 'checked' ||
        this.state === 'selected'
      ) {
        // Do nothing
      } else {
        if (this.isOutline === false) {
          if (mode === 'in') {
            this.iconHovered = true;
            this.$refs.roundedButton.style.backgroundColor = this.getColors.hoverBackgroundColor;
            this.$refs.roundedButton.style.borderColor = this.getColors.hoverBorderColor;
            this.$refs.roundedButton.style.color = this.getColors.hoverTextColor;
          } else {
            this.iconHovered = false;
            this.$refs.roundedButton.style.backgroundColor = this.getColors.backgroundColor;
            this.$refs.roundedButton.style.borderColor = this.getColors.borderColor;
            this.$refs.roundedButton.style.color = this.getColors.textColor;
          }
        }
        if (this.isOutline) {
          if (mode === 'in') {
            this.iconHovered = true;
            this.$refs.roundedButton.style.backgroundColor = this.getColors.hoverBackgroundColor;
            this.$refs.roundedButton.style.color = this.getColors.hoverTextColor;
            this.$refs.roundedButton.style.borderColor = this.getColors.hoverBorderColor;
          } else {
            this.iconHovered = false;
            this.$refs.roundedButton.style.backgroundColor = this.getColors.backgroundColor;
            this.$refs.roundedButton.style.color = this.getColors.textColor;
            this.$refs.roundedButton.style.borderColor = this.getColors.borderColor;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.rounded-button {
  text-align: center;
  > :first-child {
    height: 100%;
  }
  & * {
    user-select: none !important;
  }
  cursor: pointer;
  .svg-icon {
    display: inline-block;
    vertical-align: top;
    width: 100%;
    height: 100%;
  }

  .btn-loading {
    padding: 3px 0px;
  }
  .btn-loading-ring{
    height: 100%;
    margin: auto;
    padding: 0;
    display: flex;
    align-items: center;
  }
  .btn {
    transition: border-color 0.5s, border 0.5s, color 0.5s,
    background-color 0.5s;
    border-radius: 26px;
    position: relative;
    user-select: none;
    /*img {
        position: absolute;
        width: 20px;
        height: 20px;
        left: 10px;
        top: calc(50% - 10px);
      }*/
  }
  .icon-img {
    width: 20px;
    height: 20px;
    img {
      width: 100%;
      height: 100%;
    }
  }
  .icon-svg {
  }
  .icon-checked {
    svg {
      display: inline-block;
      vertical-align: middle;
      width: 20.7px;
      height: 27.7px;
    }
  }

  .locked-icon {
    height: 23.8px;
    width: 56.8px;
  }

  .button-text {
    //width: 100%;
    //height: 100%;
    line-height: 1.19;
    padding: 0 20px 0 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .btn-text {
    //width: 100%;
    height: 43%;
    //line-height: 1.19;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    overflow: visible;
    text-overflow: ellipsis;
    &.is-lesson-footer {
      white-space: normal;
      line-height: 20px;
      padding: 0;
    }
  }
  .enabled {
    cursor: pointer;
  }
  .disabled {
    cursor: default;
  }
  .loading {
    border: solid 2px var(--primaryBlue);
    cursor: pointer;
    padding: 0 20px 0 20px;
  }
  .checked {
    opacity: 0.4;
    border: solid 2px var(--primaryBlue);
    cursor: pointer;
  }
  .lds-ring {
    position: relative;
    width: 30px;
    height: 30px;
    //padding-top: 6px;
  }
  .lds-ring div {
    //display: block;
    position: absolute;
    top: 0;
    left: 0;
    //transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    border: 3px solid color("white");
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: var(--white) transparent transparent transparent;
  }
  .lds-ring div:nth-child(1) {
    animation-delay: -0.45s;
  }
  .lds-ring div:nth-child(2) {
    animation-delay: -0.3s;
  }
  .lds-ring div:nth-child(3) {
    animation-delay: -0.15s;
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  .button-wrapper {
    display: inline-flex;
    width: 100%;
    height: 100%;
  }
}
.icon-container {
  position: absolute;
  bottom: 0;
  top: 0;
  display: flex;
  align-items: center;
}
.button-content{
  width: 100%;
}
.locked-button{
  display: flex;
  align-items: center;
  justify-content: center;
  .btn-text{
    padding-right: 10px;
  }
}

.button-inner{
  display: flex;
}
.btn-html-text{
  padding-right: 0px !important;
  padding-left: 0px !important;
  /deep/ .full-price{
    font-size: 12px !important;
    padding-left: 2px;
  }
}
</style>
